<template>
  <div class="add-card-page">
    <header class="top-nav">
      <button type="button" class="icon-btn back-btn" @click="$router.go(-1)">
        <van-icon name="arrow-left" />
      </button>
      <h1 class="nav-title">{{ getLabel('pageAddBankCard.navTitle', 'Agregar Tarjeta Bancaria') }}</h1>
    </header>

    <main class="page-shell">
      <!-- 卡类型 -->
      <section class="field-block">
        <label class="field-label">{{ getLabel('pageAddBankCard.cardType', 'Tipo de Tarjeta') }}</label>
        <div class="dropdown-wrap card-type-wrap">
          <button type="button" class="select-trigger" @click="cardTypeOpen = !cardTypeOpen">
            <span>{{ getLabel('pageAddBankCard.bankCard', 'Tarjeta Bancaria') }}</span>
            <van-icon :name="cardTypeOpen ? 'arrow-up' : 'arrow-down'" />
          </button>
          <div v-if="cardTypeOpen" class="select-list">
            <button type="button" class="select-item active" @click="cardTypeOpen = false">
              <strong>{{ getLabel('pageAddBankCard.bankCard', 'Tarjeta Bancaria') }}</strong>
            </button>
          </div>
        </div>
      </section>

      <!-- 银行名称 -->
      <section class="field-block">
        <label class="field-label">
          {{ getLabel('pageAddBankCard.bankName', 'Nombre del Banco') }}<em class="req">*</em>
        </label>
        <div class="dropdown-wrap bank-select-wrap">
          <button type="button" class="select-trigger" @click="bankDropdownOpen = !bankDropdownOpen">
                        <span :class="{ placeholder: !selectedBankLabel }">
                            {{ selectedBankLabel || getLabel('pageAddBankCard.selectBank', 'Seleccionar Banco') }}
                        </span>
            <van-icon :name="bankDropdownOpen ? 'arrow-up' : 'arrow-down'" />
          </button>

          <div v-if="bankDropdownOpen" class="select-list bank-list">
            <div class="search-wrap">
              <van-icon name="search" class="search-icon" />
              <input
                  v-model.trim="bankKeyword"
                  type="text"
                  class="search-input"
                  :placeholder="getLabel('pageAddBankCard.searchBank', 'Buscar banco...')"
              >
            </div>
            <div v-if="!filteredBanks.length" class="list-empty">
              {{ getLabel('pageAddBankCard.noBanks', 'Sin bancos disponibles') }}
            </div>
            <button
                v-for="item in filteredBanks"
                :key="item.wid"
                type="button"
                class="select-item"
                :class="{ active: item.value === value_bank }"
                @click="selectBank(item)"
            >
              <strong>{{ item.text }}</strong>
              <small v-if="item.code">{{ item.code }}</small>
            </button>
          </div>
        </div>
      </section>

      <!-- 账户名 -->
      <section class="field-block">
        <label class="field-label">
          {{ getLabel('pageAddBankCard.accountName', 'Nombre del Titular') }}<em class="req">*</em>
        </label>
        <div class="input-wrap">
          <input
              v-model.trim="bank.name"
              type="text"
              class="text-input"
              :placeholder="getLabel('pageAddBankCard.accountNamePlaceholder', 'Ingresa el nombre del titular')"
          >
        </div>
      </section>

      <!-- 卡号 -->
      <section class="field-block">
        <label class="field-label">
          {{ getLabel('pageAddBankCard.cardNumber', 'Número de Tarjeta') }}<em class="req">*</em>
        </label>
        <div class="input-wrap">
          <input
              v-model.trim="bank.account"
              type="text"
              inputmode="numeric"
              class="text-input"
              :placeholder="getLabel('pageAddBankCard.cardNumberPlaceholder', 'Ingresa el número de tarjeta')"
          >
        </div>
      </section>

      <!-- 短信验证码 -->
      <section class="field-block">
        <label class="field-label">
          {{ getLabel('pageAddBankCard.smsCode', 'Código de Verificación') }}<em class="req">*</em>
        </label>
        <div class="input-wrap code-wrap">
          <input
              v-model.trim="bank.code"
              type="text"
              inputmode="numeric"
              class="text-input"
              :placeholder="getLabel('pageAddBankCard.smsCodePlaceholder', 'Ingresa el código SMS')"
          >
          <button
              type="button"
              class="code-btn"
              :disabled="codeSending || countdown > 0"
              @click="sendcode"
          >
            {{ countdown > 0 ? countdown + getLabel('pageAddBankCard.suffix', 's') : getLabel('pageAddBankCard.sendCode', 'Enviar Código') }}
          </button>
        </div>
      </section>

      <!-- 默认卡开关 -->
      <section class="default-row">
        <span>{{ getLabel('pageAddBankCard.setDefault', 'Establecer como tarjeta predeterminada') }}</span>
        <van-switch
            v-model="isDefault"
            size="24px"
            active-color="#2fd0e6"
            inactive-color="rgba(255,255,255,0.18)"
        />
      </section>

      <!-- 提交 -->
      <button type="button" class="confirm-btn" :disabled="submitLoading" @click="submitBtn">
        {{ submitLoading ? getLabel('utils.loading', 'Cargando...') : getLabel('pageAddBankCard.submitBtn', 'Agregar Tarjeta Bancaria') }}
      </button>

      <p class="secure-note">
        <van-icon name="shield-o" />
        <span>{{ getLabel('pageAddBankCard.secureNote', 'La información de tu tarjeta bancaria estará cifrada de forma segura') }}</span>
      </p>
    </main>
  </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Icon, Switch, Toast } from "vant";

Vue.use(Icon);
Vue.use(Switch);
Vue.use(Toast);

// 墨西哥银行编码清单（银行编码 → 银行名称），作为 Bank Name 下拉数据源
const BANK_LIST = [
  { code: 2001, name: "BANXICO" },
  { code: 37006, name: "BANCOMEXT" },
  { code: 37009, name: "BANOBRAS" },
  { code: 37019, name: "BANJERCITO" },
  { code: 37135, name: "NAFIN" },
  { code: 37166, name: "BaBien" },
  { code: 37168, name: "HIPOTECARIA FED" },
  { code: 40002, name: "BANAMEX" },
  { code: 40012, name: "BBVA MEXICO" },
  { code: 40014, name: "SANTANDER" },
  { code: 40021, name: "HSBC" },
  { code: 40030, name: "BAJIO" },
  { code: 40036, name: "INBURSA" },
  { code: 40042, name: "MIFEL" },
  { code: 40044, name: "SCOTIABANK" },
  { code: 40058, name: "BANREGIO" },
  { code: 40059, name: "INVEX" },
  { code: 40060, name: "BANSI" },
  { code: 40062, name: "AFIRME" },
  { code: 40072, name: "BANORTE" },
  { code: 40102, name: "ACCENDO BANCO" },
  { code: 40103, name: "AMERICAN EXPRES" },
  { code: 40106, name: "BANK OF AMERICA" },
  { code: 40108, name: "MUFG" },
  { code: 40110, name: "JP MORGAN" },
  { code: 40112, name: "BMONEX" },
  { code: 40113, name: "VE POR MAS" },
  { code: 40124, name: "CITI MEXICO" },
  { code: 40126, name: "CREDIT SUISSE" },
  { code: 40127, name: "AZTECA" },
  { code: 40128, name: "KAPITAL" },
  { code: 40129, name: "BARCLAYS" },
  { code: 40130, name: "COMPARTAMOS" },
  { code: 40132, name: "MULTIVA BANCO" },
  { code: 40133, name: "ACTINVER" },
  { code: 40136, name: "INTERCAM BANCO" },
  { code: 40137, name: "BANCOPPEL" },
  { code: 40138, name: "UALA" },
  { code: 40140, name: "CONSUBANCO" },
  { code: 40141, name: "VOLKSWAGEN" },
  { code: 40143, name: "CIBANCO" },
  { code: 40145, name: "BBASE" },
  { code: 40147, name: "BANKAOOL" },
  { code: 40148, name: "PAGATODO" },
  { code: 40150, name: "INMOBILIARIO" },
  { code: 40151, name: "DONDE" },
  { code: 40152, name: "BANCREA" },
  { code: 40154, name: "BANCO COVALTO" },
  { code: 40155, name: "ICBC" },
  { code: 40156, name: "SABADELL" },
  { code: 40157, name: "SHINHAN" },
  { code: 40158, name: "MIZUHO BANK" },
  { code: 40159, name: "BANK OF CHINA" },
  { code: 40160, name: "BANCO S3" },
  { code: 40167, name: "HEY BANCO" },
  { code: 90600, name: "MONEXCB" },
  { code: 90601, name: "GBM" },
  { code: 90602, name: "MASARI" },
  { code: 90605, name: "VALUE" },
  { code: 90606, name: "ESTRUCTURADORES" },
  { code: 90608, name: "VECTOR" },
  { code: 90613, name: "MULTIVA CBOLSA" },
  { code: 90616, name: "FINAMEX" },
  { code: 90617, name: "VALMEX" },
  { code: 90620, name: "PROFUTURO" },
  { code: 90630, name: "CB INTERCAM" },
  { code: 90631, name: "CI BOLSA" },
  { code: 90634, name: "FINCOMUN" },
  { code: 90636, name: "HDI SEGUROS" },
  { code: 90638, name: "NU MEXICO" },
  { code: 90642, name: "REFORMA" },
  { code: 90646, name: "STP" },
  { code: 90648, name: "TACTIV CB" },
  { code: 90652, name: "CREDICAPITAL" },
  { code: 90653, name: "KUSPIT" },
  { code: 90656, name: "UNAGRA" },
  { code: 90659, name: "ASP INTEGRA OPC" },
  { code: 90661, name: "KLAR" },
  { code: 90670, name: "LIBERTAD" },
  { code: 90676, name: "C.B. INBURSA" },
  { code: 90677, name: "CAJA POP MEXICA" },
  { code: 90680, name: "CRISTOBAL COLON" },
  { code: 90683, name: "CAJA TELEFONIST" },
  { code: 90684, name: "TRANSFER" },
  { code: 90685, name: "FONDO (FIRA)" },
  { code: 90686, name: "INVERCAP" },
  { code: 90688, name: "CREDICLUB" },
  { code: 90689, name: "FOMPED" },
  { code: 90694, name: "OPORMEX" },
  { code: 90695, name: "TE CREEMOS" },
  { code: 90696, name: "SC PROM Y OP" },
  { code: 90697, name: "CAPITAL ACTIVO" },
  { code: 90698, name: "BURSAMETRICA" },
  { code: 90699, name: "FONDEADORA" },
  { code: 90703, name: "TESORED" },
  { code: 90706, name: "ARCUS FI" },
  { code: 90710, name: "NVIO" },
  { code: 90715, name: "CASHI CUENTA" },
  { code: 90720, name: "MexPago" },
  { code: 90721, name: "albo" },
  { code: 90722, name: "Mercado Pago W" },
  { code: 90723, name: "Cuenca" },
  { code: 90725, name: "COOPDESARROLLO" },
  { code: 90728, name: "SPIN BY OXXO" },
  { code: 90729, name: "Dep y Pag Dig" },
  { code: 90732, name: "Peibo" },
  { code: 90734, name: "FINCO PAY" },
  { code: 90738, name: "FINTOC" },
  { code: 90901, name: "CLS" },
  { code: 90902, name: "INDEVAL" },
  { code: 90903, name: "CoDi Valida" },
  { code: 90996, name: "BanCobro" },
];

export default {
  name: "addcount",
  data() {
    return {
      bank: {
        bank: "",
        name: "",
        account: "",
        area: "",
        type: 4,
        banks_id: 0,
        wid: 10,
        code: "",
      },
      userMobile: "",
      codeSending: false,
      countdown: 0,
      countdownTimer: null,
      banks: [],
      value_bank: 0,
      cardTypeOpen: false,
      bankDropdownOpen: false,
      bankKeyword: "",
      isDefault: false,
      submitLoading: false,
      appOriginalStyles: null,
      bodyOriginalBackground: "",
    };
  },
  computed: {
    selectedBankLabel() {
      return this.banks[this.value_bank] ? this.banks[this.value_bank].text : "";
    },
    filteredBanks() {
      const kw = (this.bankKeyword || "").trim().toLowerCase();
      if (!kw) {
        return this.banks;
      }
      return this.banks.filter(
          (item) =>
              (item.text || "").toLowerCase().includes(kw) ||
              (item.code || "").toLowerCase().includes(kw)
      );
    },
  },
  created() {
    this.$parent.footer("user", false);
  },
  mounted() {
    this.applyFullScreenLayout();
    this.start();
    document.addEventListener("click", this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
    this.clearCountdown();
    this.restoreFullScreenLayout();
  },
  methods: {
    getLabel(key, defaultText) {
      return this.$te(key) ? this.$t(key) : defaultText;
    },
    applyFullScreenLayout() {
      const app = document.getElementById("app");
      if (!app) {
        return;
      }
      this.appOriginalStyles = {
        maxWidth: app.style.maxWidth,
        width: app.style.width,
        margin: app.style.margin,
        backgroundColor: app.style.backgroundColor,
        paddingBottom: app.style.paddingBottom,
      };
      this.bodyOriginalBackground = document.body.style.background;
      app.style.maxWidth = "100%";
      app.style.width = "100%";
      app.style.margin = "0";
      app.style.backgroundColor = "#05070f";
      app.style.paddingBottom = "0";
      document.body.style.background = "#05070f";
    },
    restoreFullScreenLayout() {
      const app = document.getElementById("app");
      if (app && this.appOriginalStyles) {
        app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
        app.style.width = this.appOriginalStyles.width || "";
        app.style.margin = this.appOriginalStyles.margin || "";
        app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
        app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
      }
      document.body.style.background = this.bodyOriginalBackground || "";
    },
    start() {
      this.banks = BANK_LIST.map((item, i) => ({
        text: item.name,
        value: i,
        code: String(item.code),
        wid: item.code,
      }));
      Fetch("/user/info").then((r) => {
        const d = (r && r.data) || {};
        this.userMobile = d.mobile || "";
      }).catch(() => {});
    },
    startCountdown(seconds = 60) {
      this.clearCountdown();
      this.countdown = seconds;
      this.countdownTimer = setInterval(() => {
        if (this.countdown <= 1) {
          this.clearCountdown();
          return;
        }
        this.countdown -= 1;
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      this.countdown = 0;
    },
    async sendcode() {
      if (this.codeSending || this.countdown > 0) return;
      console.log(this.userMobile)
      if (!this.userMobile) {
        this.$toast(this.getLabel("pageAddBankCard.noMobile", "Número de teléfono no disponible"));
        return;
      }
      this.codeSending = true;
      try {
        await Fetch("/index/register_code", {
          mobile: this.userMobile,
        });
        this.$toast(this.getLabel("pageAddBankCard.sendSuccess", "Código enviado"));
        this.startCountdown(60);
      } catch (e) {
        // Fetch 内部已处理错误
      } finally {
        this.codeSending = false;
      }
    },
    selectBank(item) {
      this.value_bank = item.value;
      this.bank.banks_id = item.wid;
      this.bank.bank = item.text;
      this.bankDropdownOpen = false;
    },
    submitBtn() {
      if (this.submitLoading) {
        return;
      }
      console.log("进这了this.submitLoading", this.bank)
      if (!this.bank.banks_id) {
        this.$toast(this.getLabel("pageAddBankCard.bankEmpty", "Selecciona un banco"));
        return;
      }
      console.log("进这了this.submitLoading", this.bank)
      if (!this.bank.name) {
        this.$toast(this.getLabel("pageAddBankCard.nameEmpty", "Ingresa el nombre del titular"));
        return;
      }
      if (!this.bank.account) {
        this.$toast(this.getLabel("pageAddBankCard.cardEmpty", "Ingresa el número de tarjeta"));
        return;
      }
      if (!this.bank.code) {
        this.$toast(this.getLabel("pageAddBankCard.codeEmpty", "Ingresa el código de verificación"));
        return;
      }
      console.log("进这了。", this.bank)
      this.submitLoading = true;
      Fetch("/user/bank_add", {
        ...this.bank,
        is_default: this.isDefault ? 1 : 0,
      })
          .then(() => {
            this.$toast.success(this.getLabel("pageAddBankCard.success", "Tarjeta agregada correctamente"));
            this.$router.push("/account");
          })
          .catch(() => {})
          .finally(() => {
            this.submitLoading = false;
          });
    },
    handleDocumentClick(event) {
      if (!this.$el) {
        return;
      }
      const cardWrap = this.$el.querySelector(".card-type-wrap");
      const bankWrap = this.$el.querySelector(".bank-select-wrap");
      if (this.cardTypeOpen && cardWrap && !cardWrap.contains(event.target)) {
        this.cardTypeOpen = false;
      }
      if (this.bankDropdownOpen && bankWrap && !bankWrap.contains(event.target)) {
        this.bankDropdownOpen = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.add-card-page {
  min-height: 100vh;
  background: #05070f;
  color: #fff;
}

.top-nav {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  padding: 0 14px;
  margin-bottom: 16px;
}

.icon-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid rgba(90, 130, 200, 0.3);
  border-radius: 50%;
  background: rgba(20, 35, 65, 0.6);
  color: #cdd8ee;
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-btn .van-icon {
  font-size: 18px;
}

.back-btn {
  position: absolute;
  left: 0;
}

.nav-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #eef3ff;
}

.page-shell {
  width: 100%;
  max-width: 430px;
  min-height: 100vh;
  margin: 0 auto;
  padding: 0 14px 40px;
  box-sizing: border-box;
  background:
      radial-gradient(circle at 80% 0%, rgba(30, 120, 200, 0.12), transparent 45%),
      #05070f;
}

.field-block {
  margin-bottom: 18px;
}

.field-label {
  display: block;
  margin: 0 0 10px 2px;
  color: #c5cfe4;
  font-size: 14px;
  font-weight: 500;
}

.field-label .req {
  margin-left: 2px;
  color: #ff4d5e;
  font-style: normal;
}

.dropdown-wrap {
  position: relative;
}

.select-trigger,
.input-wrap {
  width: 100%;
  min-height: 52px;
  padding: 0 16px;
  border: 1px solid rgba(70, 110, 190, 0.3);
  border-radius: 12px;
  background: rgba(12, 24, 46, 0.72);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.select-trigger span {
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-trigger .placeholder {
  color: #5a6f9e;
}

.select-trigger .van-icon {
  flex-shrink: 0;
  color: #8a9bb8;
  font-size: 20px;
}

.select-list {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 40;
  overflow: hidden;
  max-height: 300px;
  border: 1px solid rgba(70, 110, 190, 0.4);
  border-radius: 12px;
  background: rgba(15, 28, 52, 0.98);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
}

.bank-list {
  max-height: 280px;
  overflow-y: auto;
}

.search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.search-icon {
  flex-shrink: 0;
  color: #5a6f9e;
  font-size: 16px;
}

.search-input {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 12px;
  border: 1px solid rgba(70, 110, 190, 0.25);
  border-radius: 8px;
  outline: none;
  background: rgba(10, 20, 40, 0.6);
  color: #fff;
  font-size: 13px;
  box-sizing: border-box;
}

.search-input::placeholder {
  color: #5a6f9e;
}

.select-item {
  width: 100%;
  padding: 14px 16px;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
}

.select-item:last-child {
  border-bottom: 0;
}

.select-item strong {
  display: block;
  font-size: 14px;
  font-weight: 700;
}

.select-item small {
  display: inline-block;
  margin-top: 3px;
  color: #5a6f9e;
  font-size: 11px;
}

.select-item.active strong {
  color: #2fd0e6;
}

.list-empty {
  padding: 18px;
  color: #5a6f9e;
  font-size: 12px;
  text-align: center;
}

.text-input {
  width: 100%;
  border: 0;
  outline: none;
  background: transparent;
  color: #fff;
  font-size: 15px;
}

.text-input::placeholder {
  color: #5a6f9e;
}

.code-wrap {
  position: relative;
}

.code-wrap .text-input {
  padding-right: 120px;
}

.code-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  padding: 6px 10px;
  border: 0;
  border-radius: 8px;
  background: rgba(45, 208, 230, 0.15);
  color: #2fd0e6;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.code-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.default-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4px 2px 24px;
}

.default-row span {
  color: #e6edfb;
  font-size: 14px;
}

.confirm-btn {
  width: 100%;
  height: 54px;
  border: 0;
  border-radius: 12px;
  background: linear-gradient(90deg, #2fd0e6, #27b6d6);
  box-shadow: 0 6px 20px rgba(45, 180, 220, 0.35);
  color: #04222b;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 1px;
  cursor: pointer;
}

.confirm-btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.secure-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 14px 0 0;
  color: #6f7f9e;
  font-size: 12px;
  text-align: center;
}

.secure-note .van-icon {
  flex-shrink: 0;
  color: #2fd0e6;
  font-size: 16px;
}

@media (max-width: 360px) {
  .page-shell {
    padding-left: 10px;
    padding-right: 10px;
  }
}
</style>
